import {
  type RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Args,
  type RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Options,
  useRentalManagerUserPropertyMultiplexV2WebApiHandler_rank,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type { UseMutationOptions } from '@zg-rentals/react-query5';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-client';

/**
 * Same shape as `UserPropertyMultiplexPhotoRankRequestWebDto` from the generated API client, sourced from the multiplex
 * handler `rank` mutation args.
 */
export type UserPropertyMultiplexPhotoRankRequestWebDto =
  RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Args['postData'];

/**
 * Same shape as `UserPropertyMultiplexPhotoResponseWebDto` from the generated API client, sourced from the multiplex
 * handler `rank` mutation result type.
 */
export type UserPropertyMultiplexPhotoResponseWebDto =
  RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Options extends UseMutationOptions<
    infer Data,
    unknown,
    RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Args
  >
    ? Data
    : never;

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

/**
 * Runtime guard beyond compile-time args:
 * `postData.multiplexBuildingSessionId` must be a non-empty string; `postData.ranks` must be an array
 * ({@link UserPropertyMultiplexPhotoRankRequestWebDto}).
 */
const assertMultiplexPhotoRankMutationArgs = (
  args: RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Args,
): void => {
  const { postData } = args;

  if (typeof postData !== 'object' || postData === null || Array.isArray(postData)) {
    throw new TypeError(
      'useMultiplexPhotoRank: `postData` must be a non-array object (UserPropertyMultiplexPhotoRankRequestWebDto).',
    );
  }

  const sessionId = postData.multiplexBuildingSessionId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexPhotoRank: `postData.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (!Array.isArray(postData.ranks)) {
    throw new TypeError('useMultiplexPhotoRank: `postData.ranks` must be an array.');
  }
};

export type UseMultiplexPhotoRankOptions = Omit<
  RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexPhotoRankMutation = ReturnType<typeof useRentalManagerUserPropertyMultiplexV2WebApiHandler_rank>;

export type UseMultiplexPhotoRankResult = Omit<
  BaseMultiplexPhotoRankMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Args,
    options?: Parameters<BaseMultiplexPhotoRankMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexPhotoRankMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexV2WebApiHandler_rank_Args,
    options?: Parameters<BaseMultiplexPhotoRankMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexPhotoRankMutation['mutateAsync']>;
  data: BaseMultiplexPhotoRankMutation['data'];
  error: BaseMultiplexPhotoRankMutation['error'];
  isPending: BaseMultiplexPhotoRankMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  success: boolean | undefined;
  errorMessage: UserPropertyMultiplexPhotoResponseWebDto['errorMessage'] | undefined;
  units: UserPropertyMultiplexPhotoResponseWebDto['units'] | undefined;
};

/**
 * Multiplex photo rank: wraps the generated `rank` mutation (POST `/api/web/v2/users/properties/multiplex/photos/rank`).
 *
 * Merges default `meta`: invalidates listing recommendations (plus any `meta.invalidates` you pass) and sets
 * `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), and `success`, `errorMessage`, and `units` from the last
 * successful `data`.
 *
 * **Calling the mutation:** Use `{ postData: { multiplexBuildingSessionId, ranks } }` matching
 * {@link UserPropertyMultiplexPhotoRankRequestWebDto}. Compile-time typing applies to `mutate` /
 * `mutateAsync`; runtime checks enforce `postData`, its `ranks` array, and a non-empty session id string.
 */
const useMultiplexPhotoRank = (mutationOptions: UseMultiplexPhotoRankOptions = {}): UseMultiplexPhotoRankResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexV2WebApiHandler_rank({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      // Do not automatically invalidate multiplexBuildingSessionDetails for rank changes. PhotoGallery updates that
      // photo-source cache optimistically, then marks inactive details queries stale after the rank write settles.
      invalidates: ['listingRecommendations', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'RentalManagerUserPropertyMultiplexV2WebApiHandler',
        details: 'multiplexBuildingSessionPhotoRank',
        ...metaOverrides?.context,
      },
    },
  });

  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexPhotoRankMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexPhotoRankMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    success: data?.success,
    errorMessage: data?.errorMessage,
    units: data?.units,
  };
};

export default useMultiplexPhotoRank;
